var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-use-before-define */
// constants
import { PRODUCT_EVENTS, } from '@nykaa/utils/retina/constants';
import { PAGE_LOAD_EVENT_NAME, DEFAULT_EVENT_THRESHOLD, LISTING_PAGES_ARRAY, POST, BANNER_EVENTS, ANALYTICS_PAGE_COUNTER, ANALYTICS_EVENT_COUNTER, ANALYTICS_EVENT_TIME, ANALYTICS_SESSION_UPDATED_AT, SESSION_SEPARATOR, ANALYTICS_SESSION_MAX_REFRESH_IN_HOUR, ANALYTICS_SESSION_REFRESH_IN_MINUTES, ANALYTICS_SESSION_ID, SYSTEM_EVENTS, } from '../constants';
// helpers
import { getCommonFields, setCachedFields } from './commonFields';
import { getStoredAnalytics, storeAnalytics, clearAnalytics, getFromLS, getEventSessionData, getVisitorId, getEventForSameSession, getEventForNewSession, getDataForABConfig, getCurrentTimeStampForEvent, isSearchRedirectedPLP, } from './utils';
// local visitorIdCache for events operations
let visitorIdCache;
// Max session expiry time
let maximumSessionRefreshTimeInHour = ANALYTICS_SESSION_MAX_REFRESH_IN_HOUR;
// Session expiry time
let sessionRefreshTimeInMin = ANALYTICS_SESSION_REFRESH_IN_MINUTES;
// batch interval timer
let intervalTimer;
// session page name
let localPageName;
// session page type
let localPageType;
// all events list
let events = [];
// all impression list
let impressionEvents = [];
// analytics endpoint
let endpoint;
// Batch Events Threshold Count
let eventThresholdCount = DEFAULT_EVENT_THRESHOLD;
// Function for Logging
let customErrorLogging;
// boolean variable check if this is initialLoad
// and first event call when session resets
let isInitialLoadFirstEvent = false;
/**
 * @param eventsList eventsList list of events to be appended in returning object
 * @param additionalEvents that needs to be attached from the handler (for impressions: impressionEvents | otherwise it will be empty)
 * @returns {object} formatted events' data
 */
const getEventsData = (eventsList, additionalEvents = []) => ({
    common_fields: Object.assign(Object.assign({}, getCommonFields()), { hit_sent_at: new Date().getTime() / 1000 }),
    events: [
        ...eventsList,
        ...additionalEvents,
    ],
});
/**
 * Function to send Data to analytics backend using fetch + keepalive
 * (add in events batch only when event list passed as 2nd argument)
 * @param postApiData final events object that's needs to sent to the backend pipeline
 * @param batchEvents map which is used to batch events, when the fetch api call failed
 */
const sendDataThroughFetch = (postApiData = {}, batchEvents = []) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    // send data only when endpoint is defined and postApiData have keys
    if (!!endpoint && !!(Object.keys(postApiData).length > 0)) {
        try {
            const response = yield fetch(endpoint, {
                method: POST,
                keepalive: true,
                headers: new Headers({
                    InvocationType: 'Event',
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify(postApiData),
            });
            const resJson = yield response.json();
            // throw error incase of 200 not successful, so that batching will be done in catch block
            if (!((resJson === null || resJson === void 0 ? void 0 : resJson.status) === 200)) {
                throw new Error('Api Not SuccessFull');
            }
        }
        catch (error) {
            // eslint-disable-next-line @typescript-eslint/quotes
            const customErrorMsg = `[Analytics-Sdk] : fetch request to backend failed | pageLink: ${(_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.href} | userAgent:  ${(_b = window === null || window === void 0 ? void 0 : window.navigator) === null || _b === void 0 ? void 0 : _b.userAgent}`;
            customErrorLogging(error, customErrorMsg);
            /* check if event batch is provided and the fetch call is failed then add in events batch,
                which will be send again when page batching listener(n seconds or m events accumulated)
            */
            // eslint-disable-next-line no-extra-boolean-cast
            if (!!(batchEvents && batchEvents.length > 0)) {
                events.push(...batchEvents);
            }
        }
    }
});
/**
 * Send event data using native beacon implementation or fetch + keepAlive
 * @param {object} data - Event data
 */
export const sendAnalyticsEvent = (data) => {
    if (endpoint === undefined) {
        // logger.error('Endpoint missing - Nykaa Analytics SDK');
        return false;
    }
    if (!data) {
        return true;
    }
    const eventData = getEventsData(data);
    sendDataThroughFetch(eventData, data);
    return true;
};
/**
 * Send Queued events to DWH
 */
const sendEvents = () => {
    // if no events and no impressions are queued dont do anything
    if (events.length === 0 && impressionEvents.length === 0) {
        return;
    }
    const eventData = getEventsData(events, impressionEvents);
    // reset event list and impression list
    events = [];
    impressionEvents = [];
    if (endpoint === undefined) {
        // logger.error('Endpoint missing - Nykaa Analytics SDK');
        return;
    }
    sendDataThroughFetch(eventData);
};
/**
 * On Analytics SDK load check if any event stores in localstorage
 * If stored then send those events to DWH
 */
const onLoad = () => {
    const { events: storedEvents, commonFields } = getStoredAnalytics();
    if (storedEvents.length === 0) {
        return;
    }
    clearAnalytics();
    events = [...storedEvents, ...events];
    setCachedFields(commonFields);
    sendEvents();
};
/**
 * On browser unload if any event or impression in queue store it in localStorage
 */
const onUnload = () => {
    if (events.length === 0 && impressionEvents.length === 0) {
        return;
    }
    // create a local variable to include event and impression event and later flush it, for removing duplicates
    const eventLot = [
        ...events,
        ...impressionEvents,
    ];
    events = [];
    impressionEvents = [];
    // get any data already stored before we overwrite with new data
    const { events: cachedEvents } = getStoredAnalytics();
    const cacheEvents = [
        ...cachedEvents,
        ...eventLot,
    ];
    const cachedData = { events: cacheEvents, commonFields: getCommonFields() };
    storeAnalytics(cachedData);
};
/**
 * On Page hide event call onUnload func to handle queued events
 */
const onPageHide = () => {
    onUnload();
};
/**
 * On Visibility hidden event call onUnload func to handle queued events
 */
const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
        onUnload();
    }
};
/**
  Listen to browser pagehide and visibilitychange event to store queued events
  check if visibilityState is present in document,
  then attach it and return, otherwise pagehide is there as a fallback
 */
const attachUnloadListener = () => {
    if ('visibilityState' in document) {
        document.addEventListener('visibilitychange', onVisibilityChange);
        return;
    }
    // adding pagehide listener as fallback to visibilitychange
    window.addEventListener('pagehide', onPageHide);
};
/**
 * Clear the pagehide and visibilitychange listener
 */
const removeUnloadListener = () => {
    if ('visibilityState' in document) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        return;
    }
    window.removeEventListener('pagehide', onPageHide);
};
/**
 * Initialize Event Batching
 * @param {string} dwhEndpoint - DWH Endpoint
 * @param {number} batchInterval - Interval for which we should queue the events
 */
export const initializeBatch = (dwhEndpoint, batchInterval, eventsThreshold, customAnalyticsSdkLogger, maxSessionRefreshTime, sessionExpiryTime) => {
    endpoint = dwhEndpoint;
    eventThresholdCount = eventsThreshold;
    customErrorLogging = customAnalyticsSdkLogger;
    maximumSessionRefreshTimeInHour = maxSessionRefreshTime;
    sessionRefreshTimeInMin = sessionExpiryTime;
    visitorIdCache = getVisitorId();
    intervalTimer = window.setInterval(sendEvents, batchInterval);
    attachUnloadListener();
    onLoad();
};
/**
 * Destroy any listeners
 */
export const destroyBatch = () => {
    if (intervalTimer) {
        window.clearInterval(intervalTimer);
    }
    removeUnloadListener();
};
export const addSessionDetailsToEvent = (eventData) => {
    try {
        // Get all required values
        const currentTime = Date.now();
        const pageCounter = Number(getFromLS(ANALYTICS_PAGE_COUNTER, 1));
        const eventCounter = Number(getFromLS(ANALYTICS_EVENT_COUNTER, 1));
        const lastEventTime = Number(getFromLS(ANALYTICS_EVENT_TIME, 1));
        const lastSessionUpdatedAt = Number(getFromLS(ANALYTICS_SESSION_UPDATED_AT, 1));
        const lastEventHitDiff = Math.floor(Math.abs(lastEventTime - currentTime) / (1000 * 60)); // in minutes
        const maxSessionTimeDiff = Math.floor(Math.abs(lastSessionUpdatedAt - currentTime) / (1000 * 60 * 60)); // in hour
        const isPageView = !!((eventData === null || eventData === void 0 ? void 0 : eventData.event_name) === PAGE_LOAD_EVENT_NAME);
        // set pageName and pageType for the sdk when any pageLoad is encountered
        if (isPageView) {
            localPageName = eventData.page_name;
            localPageType = eventData.page_type;
        }
        const isNewSession = !!(!lastEventTime
            || lastEventHitDiff >= sessionRefreshTimeInMin
            || maxSessionTimeDiff >= maximumSessionRefreshTimeInHour);
        /*
          NEW-SESSION:-
           - reset sessionId if any of the below condition matches
           - lastEventTime is empty (new user)
           - no event got registered within x minutes
           - session is not refreshed for y minutes (max session time case)
        */
        if (isNewSession) {
            // session is new session so currentTime is attached with the visitor id
            const tempSessionId = `${visitorIdCache}${SESSION_SEPARATOR}${currentTime}`;
            const sessionData = getEventForNewSession(eventData, tempSessionId, isPageView, currentTime, localPageName, localPageType);
            startNewSession();
            return sessionData;
        }
        /*
          OLD-SESSION:-
            - since the session is old so use the old session saved at localStorage
        */
        const sessionId = getFromLS(ANALYTICS_SESSION_ID, '');
        return getEventForSameSession(eventData, sessionId, isPageView, currentTime, localPageName, localPageType, pageCounter, eventCounter);
    }
    catch (error) {
        // any error encountered
        return getEventSessionData(eventData, localPageName, localPageType, '', 1, 1);
    }
};
/**
 * Adding Product related data to event
 * @param {object} productEvent - event data
 * @returns {object} processed product event with page specific data
 */
export const addProductDetailsToProductEvent = (productEvent) => {
    let preparedProductEvent = Object.assign({}, productEvent);
    try {
        // getting all required data from datalayer and event
        const { datalayer = {} } = window;
        const analyticsSdkData = (datalayer === null || datalayer === void 0 ? void 0 : datalayer.analyticsSdkData) || {};
        const eventPageType = productEvent === null || productEvent === void 0 ? void 0 : productEvent.page_type;
        const searchTerm = analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.plpSearchTerm;
        const dnHybridPageType = analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.plpDnHybridPageType;
        // attaching dnHybridPageType data across events in all PLP pages
        preparedProductEvent = Object.assign(Object.assign({}, preparedProductEvent), (!!(!!dnHybridPageType && LISTING_PAGES_ARRAY.indexOf(eventPageType) > -1) && { hybrid_page_type: dnHybridPageType }));
        // attaching page related data to event, if present in the event then take it(fashion case), otherwise get from datalayer(beauty)
        switch (eventPageType) {
            case 'search_listing':
                preparedProductEvent = Object.assign(Object.assign({}, preparedProductEvent), { page_search_term: searchTerm });
                break;
            case 'category_listing':
                preparedProductEvent = Object.assign(Object.assign(Object.assign({}, preparedProductEvent), { page_category_id: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.plpCategoryId, page_category_name: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.plpCategoryName }), (isSearchRedirectedPLP() && { is_search_redirection: true }));
                break;
            case 'brand_listing':
                preparedProductEvent = Object.assign(Object.assign(Object.assign({}, preparedProductEvent), { page_brand_id: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.plpCategoryId, page_brand_name: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.plpCategoryName }), (isSearchRedirectedPLP() && { is_search_redirection: true }));
                break;
            case 'product':
                preparedProductEvent = Object.assign(Object.assign({}, preparedProductEvent), { page_product_id: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.pdpProductId, page_product_name: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.pdpProductName });
                break;
            default:
                break;
        }
    }
    catch (error) {
        preparedProductEvent = Object.assign({}, productEvent);
    }
    // finally returning the prepared product event
    return preparedProductEvent;
};
/**
 * Adding Additional data to banner event
 * @param {object} bannerEvent - event data
 * @returns {object} processed banner event with page specific data
 */
export const addDetailsToBannerEvents = (bannerEvent) => {
    let preparedBannerEvent = Object.assign({}, bannerEvent);
    try {
        const { datalayer = {} } = window;
        const analyticsSdkData = (datalayer === null || datalayer === void 0 ? void 0 : datalayer.analyticsSdkData) || {};
        const eventPageType = bannerEvent === null || bannerEvent === void 0 ? void 0 : bannerEvent.page_type;
        switch (eventPageType) {
            case 'product':
                preparedBannerEvent = Object.assign(Object.assign({}, preparedBannerEvent), { page_product_id: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.pdpProductId, page_product_name: analyticsSdkData === null || analyticsSdkData === void 0 ? void 0 : analyticsSdkData.pdpProductName });
                break;
            case 'category_listing':
            case 'brand_listing':
                preparedBannerEvent = Object.assign(Object.assign({}, preparedBannerEvent), (isSearchRedirectedPLP() && { is_search_redirection: true }));
                break;
            default:
                break;
        }
    }
    catch (error) {
        preparedBannerEvent = Object.assign({}, bannerEvent);
    }
    // finally returning the prepared banner event
    return preparedBannerEvent;
};
/**
 * Adding Additional details to events
 * @param {object} eventData - event data
 * @returns {object} event with processed data {sessiondata, banner, product, page specific data}
 */
export const addAdditionalDetailsToEvent = (eventData) => {
    var _a, _b;
    const eventName = eventData === null || eventData === void 0 ? void 0 : eventData.event_name;
    let modifiedEvent = Object.assign({}, addSessionDetailsToEvent(eventData));
    if (eventName && PRODUCT_EVENTS.indexOf(eventName) > -1) {
        modifiedEvent = addProductDetailsToProductEvent(modifiedEvent);
    }
    if (eventName && BANNER_EVENTS.indexOf(eventName) > -1) {
        modifiedEvent = addDetailsToBannerEvents(modifiedEvent);
    }
    if (eventName && [SYSTEM_EVENTS.LISTING_COMPLETE].indexOf(eventName) > -1) {
        const { datalayer = {} } = window;
        modifiedEvent = Object.assign(Object.assign(Object.assign({}, modifiedEvent), (isSearchRedirectedPLP() && { is_search_redirection: true })), { hybrid_page_type: ((_a = datalayer === null || datalayer === void 0 ? void 0 : datalayer.analyticsSdkData) === null || _a === void 0 ? void 0 : _a.plpDnHybridPageType) || '' });
    }
    if (eventName && [SYSTEM_EVENTS.SEARCH_COMPLETE].indexOf(eventName) > -1) {
        const { datalayer = {} } = window;
        modifiedEvent = Object.assign(Object.assign({}, modifiedEvent), { hybrid_page_type: ((_b = datalayer === null || datalayer === void 0 ? void 0 : datalayer.analyticsSdkData) === null || _b === void 0 ? void 0 : _b.plpDnHybridPageType) || '' });
    }
    return modifiedEvent;
};
/**
 * Callback when New Session Starts
 * @returns {object} Processed event final object
 */
export const startNewSession = () => {
    // sendABConfigEvent when session resets
    if (!isInitialLoadFirstEvent) {
        // trigger when initially called and ABConfig is send by GTM
        window.localStorage.setItem('ANALYTICS_SESSION_NEW', '1');
        isInitialLoadFirstEvent = true;
    }
    else {
        // trigger for Client side session resets then send abconfig
        const eventData = Object.assign({ hit_recorded_at: getCurrentTimeStampForEvent() }, addAdditionalDetailsToEvent({
            a_b_config_values: getDataForABConfig(),
            event_type: 'system',
            event_name: 'a_b_config',
        }));
        sendAnalyticsEvent([eventData]);
    }
};
/**
 * Batch event data
 * @param {object} data - Event data
 */
export const batchEvent = (data) => {
    events.push(Object.assign({}, data));
    if ((events.length + impressionEvents.length) >= eventThresholdCount) {
        sendEvents();
    }
};
/**
 * Batch Multiple events
 * @param {object} data - List of all events
 */
export const batchMultipleEvents = (data) => {
    events = [...events, ...data];
    if ((events.length + impressionEvents.length) >= eventThresholdCount) {
        sendEvents();
    }
};
/**
 * Batch impression events
 * @param {object} impressionData - Impression event data
 */
export const batchImpressionEvents = (impressionData) => {
    impressionEvents.push(impressionData);
    if ((events.length + impressionEvents.length) >= eventThresholdCount) {
        sendEvents();
    }
};
export const filterMultipleEventList = (eventList = [], excludedEvents = {}) => {
    let finalEventList = [];
    try {
        // Iterate eventList and filter only elements which are not present in excludedEvents
        // eslint-disable-next-line no-plusplus
        for (let index = 0; index < eventList.length; index++) {
            const currentEvent = eventList[index];
            const currentEventName = currentEvent === null || currentEvent === void 0 ? void 0 : currentEvent.event_name;
            // only exclude excludedEvents from eventList
            if (!excludedEvents[currentEventName]) {
                finalEventList.push(currentEvent);
            }
        }
    }
    catch (error) {
        finalEventList = [];
    }
    return finalEventList;
};
export const convertArrayToObject = (arr) => {
    let finalObj = {};
    try {
        finalObj = arr.reduce((accumulator, value) => (Object.assign(Object.assign({}, accumulator), { [value]: true })), {});
    }
    catch (error) {
        finalObj = {};
    }
    return finalObj;
};
